import { useEffect, useState } from "react";
import { Copy, LogOut, Users, Settings, Package, RefreshCw, Shield, BarChart3, Search, X } from "lucide-react";
import TeamMembers from "./TeamMembers";
import { toast } from "sonner";
import { listClienti, OBIETTIVO_LABEL, CLUB_BASE } from "./clubApi";
import { Btn, Card, Pill, Logo } from "./ui";
import ClientDrawer from "./ClientDrawer";
import TeamSettings from "./TeamSettings";
import Catalogo from "./Catalogo";
import StatiGuide from "./StatiGuide";
import StatsTab from "./StatsTab";

const TONE = { nuovo: "gold", scelta: "gold", riordino: "red", checkin: "blue", percorso: "green" };
const fmt = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });

export default function TeamDashboard({ user, onUser, onLogout }) {
  const [tab, setTab] = useState("clienti");
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("tutti");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); listClienti().then(setRows).catch(() => toast.error("Non riesco a caricare i clienti")).finally(() => setLoading(false)); };
  useEffect(load, []);

  const link = `${window.location.origin}${CLUB_BASE}/i/${user.slug}`;
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success("Link copiato"); } catch { toast.error("Copia non riuscita: seleziona il link e copialo a mano"); } };
  const counts = rows.reduce((a, r) => ({ ...a, [r.stato.key]: (a[r.stato.key] || 0) + 1 }), {});
  const daRisentire = rows.filter((r) => r.da_risentire).length;
  const byState = filter === "tutti" ? rows : filter === "richiamo" ? rows.filter((r) => r.da_risentire) : rows.filter((r) => r.stato.key === filter);
  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const qq = norm(q.trim());
  const shown = qq ? byState.filter((r) => norm(r.nome).includes(qq) || (r.whatsapp || "").includes(qq.replace(/\D/g, "") || "§") || norm(r.incaricata).includes(qq)) : byState;

  return (
    <div className="mx-auto max-w-3xl px-5 pt-6 pb-16" data-testid="team-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-3">
        <div className="flex items-center gap-3 min-w-0"><Logo size={34} /><div className="min-w-0"><p className="font-club-display text-lg leading-none whitespace-nowrap">Diamond Club</p><p className="text-[0.6rem] uppercase tracking-[0.22em] text-[#9A948A] mt-1 truncate">{user.role === "admin" ? "Amministratore" : "Incaricata"} · {user.name}</p></div></div>
        <div className="flex items-center gap-3 shrink-0 ml-auto">
          {user.role === "admin" && <Pill className="whitespace-nowrap" data-testid="admin-badge">Sei amministratore</Pill>}
          <button onClick={onLogout} className="text-xs text-[#9A948A] hover:text-white flex items-center gap-1" data-testid="team-logout-btn"><LogOut size={14} /> Esci</button>
        </div>
      </div>

      <Card className="mt-6 border-[#E5B13A]/30" data-testid="my-link-card">
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-[#9A948A]">Il tuo link per il test</p>
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 min-w-0 rounded-xl bg-[#0E0E10] px-3 py-2 text-sm text-[#F5D27A] break-all leading-snug" data-testid="my-link">{link.replace(/^https?:\/\//, "")}</code>
          <Btn variant="dark" className="px-4 py-2" onClick={copy} data-testid="copy-link-btn"><Copy size={14} /> Copia</Btn>
        </div>
        <p className="mt-2 text-xs text-[#6F6A62]">Mettilo in bio o mandalo su WhatsApp: «Fai il test di 3 minuti e ti preparo il percorso adatto a te, gratis».</p>
      </Card>

      <div className="mt-6 relative">
        <div className="flex gap-1 border-b border-[#26262B] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pr-8">
        {[["clienti", "Clienti", Users], ["impostazioni", "Il mio profilo", Settings], ...(user.role === "admin" ? [["catalogo", "Catalogo", Package], ["team", "Team", Shield], ["stats", "Statistiche", BarChart3]] : [])].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`team-tab-${id}`} className={`flex shrink-0 items-center gap-2 px-3 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === id ? "border-[#E5B13A] text-white" : "border-transparent text-[#9A948A] hover:text-white"}`}><Icon size={15} />{label}</button>
        ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#0E0E10] to-transparent sm:hidden" />
      </div>

      {tab === "clienti" && (
        <div className="mt-6">
          <div className="relative">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6F6A62]" />
            <input className="w-full rounded-full border border-[#2E2E34] bg-[#141417] pl-10 pr-10 py-2.5 text-sm text-[#F4EFE6] placeholder:text-[#5E5952] outline-none focus:border-[#E5B13A]/70" value={q} onChange={(e) => setQ(e.target.value)} placeholder={user.role === "admin" ? "Cerca nome, numero, incaricata" : "Cerca nome o numero"} data-testid="client-search" />
            {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6F6A62] hover:text-white" data-testid="client-search-clear"><X size={15} /></button>}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {[["tutti", `Tutti (${rows.length})`], ["nuovo", `Piano da preparare (${counts.nuovo || 0})`], ["scelta", `Attende la scelta (${counts.scelta || 0})`], ["checkin", `Check (${counts.checkin || 0})`], ["riordino", `Riordino (${counts.riordino || 0})`], ["percorso", `In percorso (${counts.percorso || 0})`], ...(daRisentire ? [["richiamo", `Da risentire (${daRisentire})`]] : [])].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${filter === k ? "border-[#E5B13A] text-[#F5D27A] bg-[#E5B13A]/10" : "border-[#2E2E34] text-[#9A948A]"}`}>{l}</button>
            ))}
            <button onClick={load} className="ml-auto text-[#9A948A] hover:text-white" title="Aggiorna" data-testid="reload-btn"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
          </div>
          <StatiGuide />
          {!loading && shown.length === 0 && (
            <Card className="mt-6 text-center text-sm text-[#9A948A]" data-testid="empty-clients">{qq ? `Nessun cliente trovato per «${q.trim()}».` : "Nessun cliente qui. Manda il tuo link: il primo test arriva da lì."}</Card>
          )}
          <div className="mt-4 space-y-2">
            {shown.map((r) => (
              <button key={r.id} onClick={() => setOpen(r.id)} data-testid={`client-row-${r.id}`} className="w-full text-left rounded-2xl border border-[#26262B] bg-[#141417] p-4 hover:border-[#E5B13A]/50 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold flex items-baseline gap-2"><span className="min-w-0 break-words">{r.nome}</span><span className="font-club-display text-[#E5B13A] shrink-0">{r.indice}</span></p>
                    <p className="text-xs text-[#9A948A] mt-0.5 leading-snug">{fmt(r.created_at)} · {r.obiettivi.map((o) => OBIETTIVO_LABEL[o] || o).join(", ")}</p>
                    {user.role === "admin" && <p className="text-xs text-[#6F6A62] mt-0.5">{r.incaricata}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Pill tone={TONE[r.stato.key]}>{r.stato.label}</Pill>
                    {r.da_risentire && <Pill tone="red" data-testid={`richiamo-pill-${r.id}`}>Da risentire</Pill>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {tab === "impostazioni" && <TeamSettings user={user} onUser={onUser} />}
      {tab === "catalogo" && <Catalogo />}
      {tab === "team" && <TeamMembers me={user} />}
      {tab === "stats" && <StatsTab />}
      {open && <ClientDrawer id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
